import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Star, Shield, Award, ArrowLeft, ExternalLink, Calendar, Clock, User, GraduationCap } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Certificate {
  id: string;
  course_id: string;
  verification_code: string;
  issued_at: string;
  user_id: string;
}

interface Profile {
  full_name: string;
  company?: string;
  job_title?: string;
  linkedin_url?: string;
}

export default function BadgeVerification() {
  const { verificationCode } = useParams<{ verificationCode: string }>();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (verificationCode) {
      loadCertificate();
    }
  }, [verificationCode]);

  const loadCertificate = async () => {
    if (!verificationCode) return;

    try {
      // Load certificate by verification code
      const { data: certData, error: certError } = await supabase
        .from('certificates')
        .select('*')
        .eq('verification_code', verificationCode)
        .maybeSingle();

      if (certError) throw certError;

      if (!certData) {
        setError('Certificate not found. Please check the verification code.');
        return;
      }

      setCertificate(certData);

      // Load profile information
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('full_name, company, job_title, linkedin_url')
        .eq('user_id', certData.user_id)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Error loading profile:', profileError);
      }

      if (profileData) {
        setProfile(profileData);
      } else {
        // If no profile found, try to get the name from the user's metadata
        console.log('No profile found, certificate user_id:', certData.user_id);
        setProfile({ full_name: 'Certified Professional' });
      }
    } catch (error) {
      console.error('Error loading certificate:', error);
      setError('Failed to load certificate details.');
    } finally {
      setLoading(false);
    }
  };

  const issueDate = certificate ? new Date(certificate.issued_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : '';

  const expirationDate = certificate ? new Date(new Date(certificate.issued_at).getTime() + (2 * 365 * 24 * 60 * 60 * 1000)).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : '';

  const recipientName = profile?.full_name || 'Professional';

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-20">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-6"></div>
              <p className="text-lg">Verifying badge...</p>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-20">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-12 text-center">
              <Shield className="h-16 w-16 text-red-500 mx-auto mb-6" />
              <h1 className="text-2xl font-bold mb-4">Verification Failed</h1>
              <p className="text-muted-foreground mb-6">
                {error || 'Certificate could not be verified.'}
              </p>
              <Button onClick={() => navigate('/')} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-8 md:py-12 pt-20 md:pt-24">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 md:mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Shield className="h-6 w-6 md:h-8 md:w-8 text-green-600" />
              <h1 className="text-2xl md:text-3xl font-bold">Verified Digital Badge</h1>
            </div>
            <p className="text-base md:text-lg text-muted-foreground px-4">
              This badge has been successfully verified and represents completion of professional training.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
            {/* Badge Display */}
            <div className="flex justify-center">
              <div className="relative w-80 h-80">
                {/* Outer Badge Container */}
                <div 
                  className="w-full h-full rounded-full shadow-2xl border-8 border-white bg-gradient-to-br from-purple-600 to-purple-800"
                  style={{ 
                    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
                    boxShadow: '0 20px 40px -8px rgba(147, 51, 234, 0.4)'
                  }}
                >
                  {/* Inner Content */}
                  <div className="w-full h-full rounded-full bg-white flex flex-col items-center justify-center text-center p-8 relative">
                    
                    {/* Top Logo/Icon */}
                    <div className="absolute top-6">
                      <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                        <Shield className="h-7 w-7 text-white" />
                      </div>
                    </div>

                    {/* Course Title */}
                    <div className="mt-12 mb-4">
                      <h3 className="text-lg font-bold text-gray-800 leading-tight mb-1">
                        AI Fundamentals &
                      </h3>
                      <h3 className="text-lg font-bold text-gray-800 leading-tight">
                        Prompt Engineering
                      </h3>
                    </div>

                    {/* Course Completed Badge */}
                    <div className="bg-purple-600 text-white px-6 py-2 rounded-full font-bold text-sm mb-4 shadow-lg">
                      COURSE COMPLETED
                    </div>

                    {/* Recipient Name */}
                    <div className="mb-4">
                      <p className="text-lg font-bold text-gray-800">
                        {recipientName}
                      </p>
                    </div>

                    {/* Organization */}
                    <div className="flex items-center gap-2 text-purple-600 font-semibold text-sm mb-2">
                      <Award className="h-4 w-4" />
                      <span>QUANTUMBOX AI</span>
                    </div>

                    {/* Date */}
                    <div className="text-xs text-gray-600 font-medium">
                      {issueDate}
                    </div>

                    {/* Verification Badge */}
                    <div className="absolute bottom-4">
                      <div className="bg-green-500 text-white text-xs px-4 py-1 rounded-full font-bold shadow-lg flex items-center gap-1">
                        <Check className="h-3 w-3" />
                        VERIFIED
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Badge Details */}
            <div className="space-y-6">
              {/* Verification Status */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-green-800">Verified Credential</h3>
                      <p className="text-sm text-green-600">This badge is authentic and verified</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 text-sm">
                    <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                      <span className="font-semibold text-foreground">Verification Code:</span>
                      <code className="bg-background px-3 py-2 rounded font-mono text-xs border font-bold">
                        {certificate.verification_code}
                      </code>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                      <span className="font-semibold text-foreground">Issue Date:</span>
                      <span className="font-medium">{issueDate}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                      <span className="font-semibold text-foreground">Expiration Date:</span>
                      <span className="font-medium">{expirationDate}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recipient Information */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Badge Recipient
                  </h3>
                   <div className="space-y-3">
                     <div className="p-4 bg-muted/30 rounded-lg border">
                       <p className="font-bold text-xl text-foreground">{recipientName}</p>
                      {profile?.job_title && profile?.company && (
                        <p className="text-sm text-muted-foreground">
                          {profile.job_title} at {profile.company}
                        </p>
                      )}
                    </div>
                    {profile?.linkedin_url && (
                      <a 
                        href={profile.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <ExternalLink className="h-3 w-3" />
                        LinkedIn Profile
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Course Information */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <GraduationCap className="h-4 w-4" />
                    Course Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium">AI Fundamentals & Prompt Engineering</h4>
                      <p className="text-sm text-muted-foreground">Level 1 Certification Program</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        <span>15 Hours</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        <span>Self-paced</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Skills & Topics Covered */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    Skills & Topics Covered
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Core Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline">AI Fundamentals</Badge>
                        <Badge variant="outline">Machine Learning</Badge>
                        <Badge variant="outline">Prompt Engineering</Badge>
                        <Badge variant="outline">Deep Learning</Badge>
                        <Badge variant="outline">Neural Networks</Badge>
                        <Badge variant="outline">AI Ethics</Badge>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Learning Outcomes</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Understanding AI fundamentals and applications</li>
                        <li>• Proficiency in prompt engineering techniques</li>
                        <li>• Knowledge of machine learning concepts</li>
                        <li>• Awareness of AI ethics and responsible AI practices</li>
                        <li>• Practical skills in AI tool utilization</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Call to Action */}
              <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
                <CardContent className="p-6 text-center">
                  <h3 className="font-semibold mb-2">Interested in AI Training?</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Join QuantumBox AI Academy and start your AI learning journey today.
                  </p>
                  <Button onClick={() => navigate('/academy')} className="w-full">
                    Explore AI Academy
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}